import type { RouteDef } from '@/app/routes'
import type { WrittenPage } from '../sections'
import { LINKS } from './links'

/**
 * Feyza Kantarcı — a personal contact page.
 *
 * Asked for on 2026-09-15 with the address to put it at
 * (`/en/about/team/feyza-kantarci`, which was a 404 until this file) and the
 * three details it should carry: her name, her mobile and her work address.
 * Nothing else was given, so nothing else is claimed — the page is a card with
 * two ways to reach her and the site's own contact band under it, rather than a
 * bio written on her behalf.
 *
 * There is no team index yet, so this page is reached from a link somebody sends
 * or from a signature. That is why it repeats the company line under her name:
 * it is often the first Digitopia page its reader sees.
 *
 * The phone is written in international form in the `tel:` href and in reading
 * form in the words, because a dialler needs `+905414725875` and a person needs
 * the spaces.
 *
 * Asked for 2026-09-15: the team contact pages should read as one set, so this
 * page follows the shared shape — a centred name, a `features` grid whose card
 * title *is* the address or the number and is itself the link, and the same
 * closing band back to the firm. The eyebrow stays "Digitopia" rather than a
 * role, because no role was given; the `check` below is still the one asking.
 */
export const route: RouteDef = {
  id: 'teamFeyzaKantarci',
  path: 'about/team/feyza-kantarci',
  // Asked for 2026-09-15 for the other contact cards and applied here for the
  // same reason: they are handed out as QR codes and links, so nothing on the
  // site should lead to them — out of the sitemap and out of search results.
  // The URL itself still works.
  robots: 'noindex',
  priority: 0.5,
}

const EMAIL = 'feyza@digitopia.ai'
const PHONE_DISPLAY = '+90 541 472 58 75'
const PHONE_DIAL = 'tel:+905414725875'
// Asked for 2026-09-17, for every team page: a button that saves the details
// straight into the reader's phone. The file is a static vCard in
// `public/vcf/`, named after this page's slug — see that folder's README.
const VCARD = '/vcf/feyza-kantarci.vcf'

export const teamFeyzaKantarci: WrittenPage = {
  navLabel: 'Feyza Kantarcı',
  title: 'Feyza Kantarcı',
  description:
    'Get in touch with Feyza Kantarcı at Digitopia: email feyza@digitopia.ai or call +90 541 472 58 75.',
  sections: [
    {
      kind: 'hero',
      eyebrow: 'Digitopia',
      headline: 'Feyza Kantarcı',
      align: 'center',
      ctas: [
        { label: 'Email', href: `mailto:${EMAIL}` },
        { label: 'Call', href: PHONE_DIAL },
        { label: 'Save contact', href: VCARD },
      ],
      check:
        'No job title was given for Feyza, so the eyebrow above her name says only "Digitopia" where the co-CEO cards carry a role. Send the title (and a headshot, if she wants one here) and both go in.',
    },
    {
      kind: 'features',
      heading: 'Get in touch',
      columns: 2,
      items: [
        {
          title: EMAIL,
          note: 'Email',
          href: `mailto:${EMAIL}`,
        },
        {
          title: PHONE_DISPLAY,
          note: 'Türkiye',
          href: PHONE_DIAL,
        },
      ],
    },
    {
      kind: 'cta',
      heading: 'Rather start with the firm than a person?',
      sub: 'The contact form reaches the whole team, and About Us says who else you would be working with.',
      tone: 'band',
      ctas: [
        { label: 'Contact Digitopia', href: LINKS.contact },
        { label: 'About Digitopia', href: LINKS.about },
      ],
    },
  ],
}
